import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Upload, File, ChevronLeft, ChevronRight } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import type { 
  PlanUploadState, 
  PlanUploadProps, 
  ValidationResult, 
  PlanUploadPaginationInfo 
} from '../../../../types/planUpload';
import type { PlanData } from '../../../../types/planDownload';
import { mockPlanData } from '../../../../data/mockPlanData';
import '../../../../styles/planUpload.css';

const PlanUpload: React.FC<PlanUploadProps> = ({ className = '' }) => {
  const [state, setState] = useState<PlanUploadState>({
    uploadedFile: null,
    planData: [],
    isUploading: false,
    isValidating: false,
    uploadProgress: 0,
    validationStatus: 'idle',
    validationResults: [],
    isPreviewExpanded: false,
    showDataTable: false,          // Initially false - table only shows after validation
    previewEnabled: false,         // Initially disabled - only enabled after validation
    currentPage: 1,
    itemsPerPage: 10,
    totalRecords: 0,
    hasFileSelected: false,
    // Toast notification properties
    showValidationToast: false,
    showUploadToast: false,
    toastMessage: '',
    toastType: null,
    uploadComplete: false
  });

  // Toast timer reference for cleanup
  const toastTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Toast notification helper function
  const showToast = useCallback((type: 'validation' | 'upload', message: string) => {
    // Clear any existing timer
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }

    // Show toast
    setState(prev => ({
      ...prev,
      showValidationToast: type === 'validation',
      showUploadToast: type === 'upload',
      toastMessage: message,
      toastType: type
    }));

    // Auto-hide after 5 seconds
    toastTimerRef.current = setTimeout(() => {
      setState(prev => ({
        ...prev,
        showValidationToast: false,
        showUploadToast: false,
        toastType: null,
        // Additional cleanup for upload completion
        ...(type === 'upload' && {
          uploadProgress: 0,
          uploadComplete: true
        })
      }));
    }, 5000);
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  // File upload handling
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setState(prev => ({
        ...prev,
        uploadedFile: file,
        hasFileSelected: true,
        validationStatus: 'idle',
        validationResults: [],
        planData: [],
        totalRecords: 0,
        currentPage: 1,
        showDataTable: false,        // Reset data table visibility
        previewEnabled: false,       // Disable preview until validation
        isPreviewExpanded: false,    // Reset expansion state
        // Reset toast-related state
        showValidationToast: false,
        showUploadToast: false,
        toastMessage: '',
        toastType: null,
        uploadComplete: false
      }));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv']
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: false,
    disabled: state.isUploading || state.isValidating
  });

  // Mock file parsing - in real implementation, this would parse the actual file
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const parseFileData = useCallback((_file: File): Promise<PlanData[]> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        // Simulate parsing and return mock data
        // File reference removed - was unused placeholder for future implementation
        const parsedData = mockPlanData.slice(0, 20); // Use first 20 items as sample
        resolve(parsedData);
      }, 1500);
    });
  }, []);

  // File validation
  const handleValidate = useCallback(async () => {
    if (!state.uploadedFile) return;

    // Start validation process
    setState(prev => ({
      ...prev,
      isValidating: true,
      validationStatus: 'validating',
      validationResults: [],
      showDataTable: false,
      previewEnabled: false,
      isPreviewExpanded: false
    }));

    try {
      // Parse file data
      const parsedData = await parseFileData(state.uploadedFile);
      
      // Generate detailed validation results
      const validationResults: ValidationResult[] = [
        {
          type: 'success',
          message: `File format (${state.uploadedFile.name.split('.').pop()?.toUpperCase()}) is supported`
        },
        {
          type: 'success',
          message: `File size (${(state.uploadedFile.size / 1024).toFixed(1)} KB) is within limits`
        },
        {
          type: 'success',
          message: 'Document structure is valid'
        },
        {
          type: 'success',
          message: 'Required fields are present'
        },
        {
          type: 'success',
          message: 'Data formatting is correct'
        }
      ];

      // Update state with successful validation
      setState(prev => ({
        ...prev,
        isValidating: false,
        validationStatus: 'success',
        validationResults,
        planData: parsedData,
        totalRecords: parsedData.length,
        showDataTable: true,           // Enable data table display
        previewEnabled: true,          // Enable preview toggle
        isPreviewExpanded: true        // Auto-expand after validation
      }));

      // Show validation success toast
      showToast('validation', 'File validated and ready for upload');

    } catch (error) {
      console.error('Validation error:', error);
      setState(prev => ({
        ...prev,
        isValidating: false,
        validationStatus: 'error',
        validationResults: [
          {
            type: 'error',
            message: 'Failed to validate file',
            details: ['Please check file format and try again']
          }
        ],
        showDataTable: false,
        previewEnabled: false,
        isPreviewExpanded: false
      }));
    }
  }, [state.uploadedFile, parseFileData, showToast]);

  // File upload
  const handleUpload = useCallback(async () => {
    if (!state.uploadedFile || state.validationStatus !== 'success') return;

    setState(prev => ({
      ...prev,
      isUploading: true,
      uploadProgress: 0
    }));

    // Simulate upload progress
    const progressInterval = setInterval(() => {
      setState(prev => {
        const newProgress = prev.uploadProgress + 10;
        if (newProgress >= 100) {
          clearInterval(progressInterval);
          
          // Show upload success toast
          showToast('upload', 'File successfully uploaded to system');
          
          return {
            ...prev,
            uploadProgress: 100,
            isUploading: false
          };
        }
        return {
          ...prev,
          uploadProgress: newProgress
        };
      });
    }, 200);
  }, [state.uploadedFile, state.validationStatus, showToast]);

  // Remove file
  const handleRemoveFile = useCallback(() => {
    // Clear any existing timer
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }

    setState(prev => ({
      ...prev,
      uploadedFile: null,
      hasFileSelected: false,
      planData: [],
      totalRecords: 0,
      validationStatus: 'idle',
      validationResults: [],
      isPreviewExpanded: false,
      showDataTable: false,
      previewEnabled: false,
      uploadProgress: 0,
      currentPage: 1,
      // Reset toast-related state
      showValidationToast: false,
      showUploadToast: false,
      toastMessage: '',
      toastType: null,
      uploadComplete: false
    }));
  }, []);

  // Toggle preview expansion
  const handleTogglePreview = useCallback(() => {
    // Only allow toggle if preview is enabled (after successful validation)
    if (!state.previewEnabled) return;
    
    setState(prev => ({
      ...prev,
      isPreviewExpanded: !prev.isPreviewExpanded
    }));
  }, [state.previewEnabled]);

  // Pagination
  const paginationInfo = useMemo((): PlanUploadPaginationInfo => {
    const totalRecords = state.planData.length;
    const totalPages = Math.ceil(totalRecords / state.itemsPerPage);
    const startRecord = (state.currentPage - 1) * state.itemsPerPage + 1;
    const endRecord = Math.min(state.currentPage * state.itemsPerPage, totalRecords);

    return {
      currentPage: state.currentPage,
      totalPages,
      totalRecords,
      itemsPerPage: state.itemsPerPage,
      startRecord: totalRecords > 0 ? startRecord : 0,
      endRecord: totalRecords > 0 ? endRecord : 0
    };
  }, [state.planData.length, state.currentPage, state.itemsPerPage]);

  const currentPageData = useMemo(() => {
    const startIndex = (state.currentPage - 1) * state.itemsPerPage;
    const endIndex = startIndex + state.itemsPerPage;
    return state.planData.slice(startIndex, endIndex);
  }, [state.planData, state.currentPage, state.itemsPerPage]);

  const handlePageChange = useCallback((page: number) => {
    setState(prev => ({
      ...prev,
      currentPage: page
    }));
  }, []);

  // Render toast notification
  const renderToastNotification = () => {
    if (!state.showValidationToast && !state.showUploadToast) {
      return null;
    }

    return (
      <div className={`toast-notification ${state.toastType} ${
        state.showValidationToast || state.showUploadToast ? 'visible' : 'hidden'
      }`}>
        <div className="toast-content">
          <div className="toast-icon">
            ✅
          </div>
          <div className="toast-text">
            <div className="toast-message">
              {state.toastType === 'validation' && 'Validation Success:'}
              {state.toastType === 'upload' && 'Upload Complete:'}
            </div>
            <div className="toast-description">{state.toastMessage}</div>
          </div>
        </div>
      </div>
    );
  };

  // Render upload progress
  const renderUploadProgress = () => {
    // Hide progress bar if upload is complete and toast has been shown
    if (!state.isUploading && (state.uploadProgress === 0 || state.uploadComplete)) {
      return null;
    }
  };

  // Render pagination
  const renderPagination = () => {
    if (!state.isPreviewExpanded || paginationInfo.totalRecords === 0) {
      return null;
    }

    return (
      <div className="plan-download-pagination">
        <div className="pagination-info">
          Showing {paginationInfo.startRecord} to {paginationInfo.endRecord} of {paginationInfo.totalRecords} records
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(paginationInfo.currentPage - 1)}
            disabled={paginationInfo.currentPage === 1}
          >
            <ChevronLeft size={16} />
          </button>
          
          {Array.from({ length: paginationInfo.totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={`pagination-number ${page === paginationInfo.currentPage ? 'active' : ''}`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}
          
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(paginationInfo.currentPage + 1)}
            disabled={paginationInfo.currentPage === paginationInfo.totalPages}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  };

  // Render file info card (stays after upload zone)
  const renderFileInfoCard = () => {
    if (!state.uploadedFile) {
      return null;
    }

    return (
    <div className="file-preview-section">
        <div className="file-info">
          <div className="file-icon">
            <File size={20} />
          </div>
          <div className="file-details">
            <div className="file-name">{state.uploadedFile.name}</div>
            <div className="file-size">
              {(state.uploadedFile.size / 1024).toFixed(1)} KB
            </div>
          </div>
          <button className="remove-file" onClick={handleRemoveFile}>
            ×
          </button>
        </div>
        </div>
    );
  };

  // Render data table inside preview section
  const renderDataTable = () => {
    // Only show data table after successful validation AND when expanded
    if (!state.showDataTable || !state.isPreviewExpanded || state.planData.length === 0) {
      return null;
    }

    return (
      <div className="plan-data-table-container">
        <table className="plan-data-table">
          <thead className="plan-data-table-header">
            <tr>
              <th>PRODUCT CODE</th>
              <th>PLAN CODE</th>
              <th>HBS PLAN ID</th>
              <th>HBS REV NO</th>
              <th>PLAN DESCRIPTION</th>
              <th>EFFECTIVE DATE</th>
              <th>EXPIRATION DATE</th>
              <th>MIN DAYS</th>
              <th>MAX DAYS</th>
              <th>CURRENCY</th>
              <th>GROSS PREMIUM</th>
              <th>NET PREMIUM</th>
              <th>MRL</th>
              <th>COVERAGE CODE</th>
              <th>AVAILABLE FOR SALES AGENT</th>
              <th>OCC TEMPLATE</th>
              <th>IS CANCELLED</th>
              <th>OCC PREFIX</th>
              <th>TACTIC TEMPLATE</th>
              <th>APP FORM TEMPLATE</th>
              <th>PLAN TAX</th>
              <th>LOCAL TAX</th>
              <th>DST</th>
            </tr>
          </thead>
          <tbody className="plan-data-table-body">
            {currentPageData.map((item) => (
              <tr key={item.id} className="plan-data-row">
                <td>{item.productCode}</td>
                <td>{item.planCode}</td>
                <td>{item.hisPlanId}</td>
                <td>{item.hisRevNo}</td>
                <td>{item.planDescription}</td>
                <td>{item.effectiveDate}</td>
                <td>{item.expirationDate}</td>
                <td>{item.minimumDays}</td>
                <td>{item.maximumDays}</td>
                <td>{item.currency}</td>
                <td>{item.grossPremium.toFixed(2)}</td>
                <td>{item.netPremium}</td>
                <td>{item.mrl}</td>
                <td>{item.coverageCode}</td>
                <td>{item.availableForSalesAgent ? 'TRUE' : 'FALSE'}</td>
                <td>{item.occTemplate}</td>
                <td>{item.isCancelled ? 'TRUE' : 'FALSE'}</td>
                <td>{item.occPrefix}</td>
                <td>{item.tacticTemplate}</td>
                <td>{item.appFormTemplate}</td>
                <td>{item.planTax}</td>
                <td>{item.localTax}</td>
                <td>{item.gst}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render file preview section (now contains both header and table)
  const renderFilePreviewSection = () => {
    // Only show when file is uploaded and data table should be visible
    if (!state.uploadedFile || !state.showDataTable) {
      return null;
    }

    return (
      <div className="file-preview-section">
        {/* File Preview Header */}
        <div className="file-preview-header">
          <div
            className={`file-preview-toggle ${!state.previewEnabled ? 'disabled' : ''}`}
            onClick={state.previewEnabled ? handleTogglePreview : undefined}
          >
            <span className="preview-toggle-text">File Preview</span>
            <button
              className={`expand-collapse-btn ${!state.previewEnabled ? 'disabled' : ''}`}
              disabled={!state.previewEnabled}
            >
              {state.isPreviewExpanded ? 'Collapse' : 'Expand'}
            </button>
          </div>
        </div>

        {/* Data Table - Shows when expanded */}
        {state.isPreviewExpanded && (
          <div className="data-preview-content">
            {renderDataTable()}
            
            {/* Upload Progress Bar - Shows during upload */}
            {state.isUploading && renderUploadProgress()}
            
            {renderPagination()}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`plan-upload ${className}`}>
      {/* Toast Notification */}
      {renderToastNotification()}
      
      {/* Header */}
      <div className="plan-upload-header">
        <div className="plan-upload-header-left">
          <div className="plan-upload-icon">
            <Upload size={24} />
          </div>
          <div className="plan-upload-title-section">
            <h1 className="plan-upload-title">Plan Upload</h1>
            <div className="plan-upload-context">
              Upload and manage your plan documents
            </div>
          </div>
        </div>
      </div>

      {/* File Upload Zone */}
      <div
        {...getRootProps()}
        className={`file-upload-zone ${isDragActive ? 'drag-active' : ''} ${
          state.isUploading || state.isValidating ? 'uploading' : ''
        }`}
      >
        <input {...getInputProps()} />
        <div className="upload-content">
          <div className="upload-icon">
            <File size={32} />
          </div>
          {state.isValidating ? (
            <>
              <p className="upload-text">Processing file...</p>
              <p className="upload-subtext">Please wait while we validate your data</p>
            </>
          ) : (
            <>
              <p className="upload-text">Drop your file here or click to browse</p>
              <p className="upload-subtext">
                Supports PDF, DOC, DOCX, XLS, XLSX files up to 10MB
              </p>
            </>
          )}
        </div>
      </div>

      {/* File Rejections Error */}
      {fileRejections.length > 0 && (
        <div className="upload-error">
          {fileRejections.map(({ file, errors }) => (
            <div key={file.name}>
              <p>❌ {file.name}:</p>
              <ul>
                {errors.map((e) => (
                  <li key={e.code}>{e.message}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* File Info Card */}
      {renderFileInfoCard()}

      {/* File Preview Section - Contains header and table */}
      {renderFilePreviewSection()}

      {/* Action Buttons */}
      <div className="upload-actions">
        <button
          className={`btn-validate ${state.isValidating ? 'loading' : ''}`}
          onClick={handleValidate}
          disabled={!state.hasFileSelected || state.isValidating || state.isUploading}
        >
          {state.isValidating ? (
            <>
              <div className="button-spinner"></div>
              Validating...
            </>
          ) : (
            'Validate'
          )}
        </button>
        <button
          className={`btn-upload-file ${state.isUploading ? 'uploading' : ''}`}
          onClick={handleUpload}
          disabled={
            state.validationStatus !== 'success' ||
            state.isUploading ||
            state.isValidating
          }
        >
          {state.isUploading ? (
            <>
              <div className="button-spinner"></div>
              Uploading...
            </>
          ) : (
            'Upload File'
          )}
        </button>
      </div>
    </div>
  );
};

export default PlanUpload;